<template>
    <div>
        <h3>Review</h3>

        <div class="row mb-3">
            <div class="col-12 col-md-3">
                <strong>Customer Name</strong>
            </div>
            <div class="col-12 col-md-9">
                {{ store.customerName }}
            </div>
        </div>

        <div class="row mb-3">
            <div class="col-12 col-md-3">
                <strong>Customer Number</strong>
            </div>
            <div class="col-12 col-md-9">
                {{ store.customerNumber }}
            </div>
        </div>

        <div class="row">
            <div class="col-12">
                <h4>Invoice{{ store.invoices.length > 1 ? 's' : '' }}</h4>
            </div>
        </div>

        <div class="row">
            <div class="col-3">
                <strong>Invoice Number</strong>
            </div>
            <div class="col-9">
                <strong>Amount</strong>
            </div>
        </div>

        <div class="row" v-for="invoice in store.invoices">
            <div class="col-3">
                {{ invoice.number }}
            </div>
            <div class="col-9">
                {{ formatCurrency(invoice.amount) }}
            </div>
        </div>

        <div class="row mb-3">
            <div class="col-3">
                <strong>Total</strong>
            </div>
            <div class="col-9">
                {{ formatCurrency(invoiceTotal) }}
            </div>
        </div>

        <div class="row mb-3">
            <div class="col-12 col-md-3">
                <strong>Billing</strong>
            </div>
            <div class="col-12 col-md-9">
                {{ store.firstName }} {{ store.lastName }}<br />
                {{ store.address1 }}<br />
                {{ store.locality }}, {{ store.region }} {{ store.postalCode }} {{ store.country }}
            </div>
        </div>

        <div class="row mb-3">
            <div class="col-sm-12">
                <div class="d-flex flex-column align-items-center">
                    <recaptcha ref="recaptchaEl" :site-key="store.recaptchaSiteKey" @solved="recaptchaCallback"></recaptcha>
                </div>
            </div>
        </div>

        <div v-if="paymentError" class="bg-white">
            <div class="text-danger">There was an error processing your payment. See details below:</div>
            <div class="text-danger">Status: {{ paymentErrorStatus }}</div>
            <div class="text-danger">Reason: {{ paymentErrorReason }}</div>
            <div class="text-danger">Message: {{ paymentErrorMessage }}</div>
        </div>

        <div class="text-center mb-3">
            <button type="button" class="btn btn-outline-primary me-3" @click="goBackToPage('PAYMENT')">Back</button>
            <button type="button" class="btn btn-primary" @click="submitPayment" :disabled="!submitEnabled">Submit Payment <i v-if="submittingPayment" class="fa-solid fa-spinner fa-spin"></i></button>
        </div>


    </div>
</template>

<script setup>
    import { computed, ref } from 'vue';
    import _ from 'lodash';
    import { useStore } from './store';
    import recaptcha from '../../../components/recaptcha';

    const emit = defineEmits(['goToPage', 'goBackToPage']);

    const recaptchaResult = ref('');
    const recaptchaPassed = ref(false);
    const recaptchaEl = ref(null);

    const submittingPayment = ref(false);

    const paymentError = ref(false);
    const paymentErrorStatus = ref('');
    const paymentErrorReason = ref('');
    const paymentErrorMessage = ref('');

    const store = useStore();

    const submitEnabled = computed(() => {
        return (recaptchaPassed.value && !submittingPayment.value);
    });

    function goToPage(page) {
        emit('goToPage', page);
    }

    function goBackToPage(page) {
        emit('goBackToPage', page);
    }

    function formatCurrency(amount) {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    }

    const invoiceTotal = computed(() => {
        var total = 0;

        _.forEach(store.invoices, invoice => {
            total += invoice.amount;
        });

        return total;
    });

    function recaptchaCallback(newState) {
        recaptchaPassed.value = !!newState;

        recaptchaResult.value = newState ? recaptchaEl.value.getResponse() : null;
    }

    function submitPayment() {
        var formData = new FormData();

        formData.append('g-recaptcha-response', recaptchaResult.value);
        formData.append('customerName', store.customerName);
        formData.append('customerNumber', store.customerNumber);
        formData.append('emailAddress', store.emailAddress);
        formData.append('paymentFirstName', store.firstName);
        formData.append('paymentLastName', store.lastName);
        formData.append('paymentAddress1', store.address1);
        formData.append('paymentLocality', store.locality);
        formData.append('paymentRegion', store.region);
        formData.append('paymentPostalCode', store.postalCode);
        formData.append('paymentCountry', store.country);
        formData.append('emailToken', store.emailToken);
        formData.append('jwt', store.flexResponse);

        store.invoices.forEach((invoice, index) => {
            Object.keys(invoice).forEach(key => {
                formData.append(`Invoices[${index}].${key}`, invoice[key]);
            });
        });

        submittingPayment.value = true;

        $.ajax({
            type: "POST",
            url: store.submitPaymentUrl,
            data: formData,
            processData: false,
            contentType: false,
            success: function (response) {

                if (response.Status == 'AUTHORIZED') {
                    store.paymentId = response.ResponseId;
                    submittingPayment.value = false;
                    goToPage('FINISH');
                }
                else {
                    paymentError.value = true;
                    paymentErrorStatus.value = response.Status;
                    paymentErrorReason.value = response.Reason;
                    paymentErrorMessage.value = response.Message;
                    submittingPayment.value = false;
                }
            }
        });
    }

</script>